import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
} from "reactstrap";
import { getProfile, updateProfile } from "store/slices/clubSlice";
import React from "react";
import { toast } from "react-toastify";
import { API_URL } from "constants/ApiUrl";
import { useTranslation } from "react-i18next";

const EditClubProfile = ({ onCancel }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { club, loading, error } = useSelector((state) => state.Club);

  const [form, setForm] = useState({
    nameAr: "",
    nameFr: "",
    city: "",
    category: "",
    description: "",
    managerName: "",
    email: "",
    managerRole: "",
    managerRoleOther: "",
    phone: "",
    logo: "",
  });

  useEffect(() => {
    if (club) {
      setForm({
        nameAr: club.nameAr || "",
        nameFr: club.nameFr || "",
        city: club.city || "",
        category: club.category || "",
        description: club.description || "",
        managerName: club.managerName || "",
        email: club.email || "",
        managerRole: club.managerRole || "",
        managerRoleOther: club.managerRoleOther || "",
        phone: club.phone || "",
        logo: "",
      });
    }
  }, [club]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        setForm((prev) => ({ ...prev, logo: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.managerRole === "Other" && !form.managerRoleOther.trim()) {
      toast.error("Veuillez renseigner le rôle du gestionnaire.");
      return;
    }

    const formData = new FormData();
    formData.append("nameAr", form.nameAr);
    formData.append("nameFr", form.nameFr);
    formData.append("city", form.city);
    formData.append("category", form.category);
    formData.append("description", form.description);
    formData.append("managerName", form.managerName);
    formData.append("email", form.email);
    formData.append("managerRole", form.managerRole);
    formData.append("phone", form.phone);
    if (form.logo) formData.append("logo", form.logo);
    if (form.managerRole === "Other") {
      formData.append("managerRoleOther", form.managerRoleOther);
    }

    try {
      const res = await fetch(`${API_URL}/api/club/profile`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authUser")}`,
        },
        body: formData,
      });

      if (res.ok) {
        dispatch(getProfile());
        onCancel();
        toast.success("Profil mis à jour avec succès");
      } else {
        const errorData = await res.json();
        toast.error(`Erreur : ${errorData.message || res.statusText}`);
      }
    } catch (error) {
      toast.error(`Erreur lors de la mise à jour : ${error.message}`);
    }
  };
  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup>
        <Label for="nameAr">{t("editProfile.clubNameAr")}</Label>
        <Input
          id="nameAr"
          name="nameAr"
          value={form.nameAr}
          onChange={handleChange}
        />
      </FormGroup>
      <FormGroup>
        <Label for="nameFr">{t("editProfile.clubNameFr")}</Label>
        <Input
          id="nameFr"
          name="nameFr"
          value={form.nameFr}
          onChange={handleChange}
          required
        />
      </FormGroup>
      <FormGroup>
        <Label for="city">{t("editProfile.city")}</Label>
        <Input
          id="city"
          name="city"
          value={form.city}
          onChange={handleChange}
        />
      </FormGroup>
      <FormGroup>
        <Label for="category">{t("editProfile.category")}</Label>
        <Input
          id="category"
          name="category"
          type="select"
          value={form.category}
          onChange={handleChange}
        >
          <option value="">{t("editProfile.selectPlaceholder")}</option>
          <option value="Amateur">Amateur</option>
          <option value="Semi-pro">Semi-pro</option>
          <option value="Professional">Professional</option>
        </Input>
      </FormGroup>
      <FormGroup>
        <Label for="description">{t("editProfile.description")}</Label>
        <Input
          id="description"
          name="description"
          type="textarea"
          value={form.description}
          onChange={handleChange}
          minLength={10}
        />
      </FormGroup>
      <FormGroup>
        <Label for="managerName">{t("editProfile.managerName")}</Label>
        <Input
          id="managerName"
          name="managerName"
          value={form.managerName}
          onChange={handleChange}
        />
      </FormGroup>
      <FormGroup>
        <Label for="email">{t("editProfile.email")}</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
        />
      </FormGroup>
      <FormGroup>
        <Label for="managerRole">{t("editProfile.managerRole")}</Label>
        <Input
          id="managerRole"
          name="managerRole"
          type="select"
          value={form.managerRole}
          onChange={handleChange}
        >
          <option value="">{t("editProfile.selectPlaceholder")}</option>
          <option value="President">{t("editProfile.roleOptions.President")}</option>
          <option value="Manager">{t("editProfile.roleOptions.Manager")}</option>
          <option value="Recruiter">{t("editProfile.roleOptions.Recruiter")}</option>
          <option value="Other">{t("editProfile.roleOptions.Other")}</option>
        </Input>
      </FormGroup>
      {form.managerRole === "Other" && (
        <FormGroup>
          <Label for="managerRoleOther">{t("editProfile.managerRoleOther")}</Label>
          <Input
            id="managerRoleOther"
            name="managerRoleOther"
            value={form.managerRoleOther}
            onChange={handleChange}
            required
          />
        </FormGroup>
      )}
      <FormGroup>
        <Label for="phone">{t("editProfile.phone")}</Label>
        <Input
          id="phone"
          name="phone"
          value={form.phone}
          onChange={handleChange}
        />
      </FormGroup>
      <FormGroup>
        <Label for="logo">{t("editProfile.logo")}</Label>
        <Input
          id="logo"
          name="logo"
          type="file"
          accept="image/*"
          onChange={handleLogoChange}
        />
      </FormGroup>
      <Button type="submit" disabled={loading}>
        {loading ? <Spinner size="sm" /> : t("editProfile.save")}
      </Button>{" "}
      <Button
        type="button"
        color="secondary"
        onClick={onCancel}
        disabled={loading}
      >
        {t("editProfile.cancel")}
      </Button>
    </Form>
  );
};

export default EditClubProfile;
